/** Default visitor-facing copy when a form has been closed and the admin set no custom message. */
export const DEFAULT_CLOSED_MESSAGE = "This form is no longer accepting responses.";

/**
 * Resolves the visitor-facing copy for a closed form: the admin's custom message,
 * or {@link DEFAULT_CLOSED_MESSAGE} when it is unset or blank.
 *
 * @param closedMessage - The form's `closed_message` column.
 * @returns The message to show visitors.
 * @throws Never throws.
 */
export function resolveClosedMessage(closedMessage: string | null | undefined): string {
  return closedMessage?.trim() ? closedMessage : DEFAULT_CLOSED_MESSAGE;
}

/**
 * Returns true when a form should reject new responses right now.
 *
 * A form is "effectively closed" if either:
 * - the admin has flipped the manual `is_closed` toggle on, OR
 * - a scheduled `closes_at` has already passed.
 *
 * This is the single source of truth used by the public route gate, the
 * save-response API gate, and the renderer's defense-in-depth banner — they
 * all call this so the answer is consistent across surfaces.
 *
 * @param form - Minimal form shape with the two close-state columns.
 * @returns `true` if the form is closed to new responses, `false` otherwise.
 */
export function isFormEffectivelyClosed(form: { is_closed?: boolean | null; closes_at?: string | null }): boolean {
  if (form.is_closed) return true;
  if (!form.closes_at) return false;
  const closesAtMs = Date.parse(form.closes_at);
  if (!Number.isFinite(closesAtMs)) return false;
  return closesAtMs < Date.now();
}
